import { useMemo, useState } from 'react';
import { useBestiaryStore } from '../../store/bestiarySlice';
import { useMenuNavigation } from '../../hooks/menu/useMenuNavigation';
import { closeInterface } from '../../services/InterfaceService';
import {
  getKilledMonsterCards,
  BestiaryCardData,
} from '../../utils/bestiary.utils';
import { BestiaryCard } from './BestiaryCard';
import { BestiaryCardLightbox } from './BestiaryCardLightbox';
import { KeyDisplay } from './KeyDisplay';
import { NineSliceFrame } from './NineSliceFrame';
import './BestiaryScreen.scss';

const COLS_PER_PAGE = 2;
const ROWS = 2;
const CARDS_PER_PAGE = COLS_PER_PAGE * ROWS;
const CARDS_PER_SPREAD = CARDS_PER_PAGE * 2;

/** Spread index (0..7) → grid row/col. Pages fill left (0-3) then right (4-7). */
function gridPos(i: number): { row: number; col: number } {
  const page = i < CARDS_PER_PAGE ? 0 : 1;
  const j = i % CARDS_PER_PAGE;
  return {
    row: Math.floor(j / COLS_PER_PAGE),
    col: page * COLS_PER_PAGE + (j % COLS_PER_PAGE),
  };
}

export const BestiaryScreen = () => {
  const entries = useBestiaryStore((s) => s.entries);
  const cards = useMemo(() => getKilledMonsterCards(entries), [entries]);

  const [offset, setOffset] = useState(0);
  const [lightbox, setLightbox] = useState<BestiaryCardData | null>(null);

  const spread = cards.slice(offset, offset + CARDS_PER_SPREAD);

  const { containerRef, focusedRow, setFocus } = useMenuNavigation({
    disabled: lightbox !== null,
    onConfirm: (id) => {
      const card = cards.find((c) => c.config.configId === id);
      if (card) {
        setLightbox(card);
      }
    },
    onCancel: () => closeInterface(),
    // onLeft/onRight cover single-column rows (a partial last page's bottom
    // row); onEdgeLeft/onEdgeRight cover the edges of full multi-column rows.
    onLeft: () => pageBack(),
    onRight: () => pageForward(),
    onEdgeLeft: () => pageBack(),
    onEdgeRight: () => pageForward(),
  });

  const spreadHasCell = (targetOffset: number, row: number, col: number) => {
    const page = col < COLS_PER_PAGE ? 0 : 1;
    const i =
      page * CARDS_PER_PAGE + row * COLS_PER_PAGE + (col % COLS_PER_PAGE);
    return targetOffset + i < cards.length;
  };

  const pageForward = () => {
    const target = offset + CARDS_PER_SPREAD;
    if (target < cards.length) {
      setOffset(target);
      setFocus(spreadHasCell(target, focusedRow, 0) ? focusedRow : 0, 0);
    }
  };

  const pageBack = () => {
    if (offset > 0) {
      const target = offset - CARDS_PER_SPREAD;
      const lastCol = COLS_PER_PAGE * 2 - 1;
      setFocus(
        spreadHasCell(target, focusedRow, lastCol) ? focusedRow : 0,
        lastCol,
      );
      setOffset(target);
    }
  };

  const renderPage = (pageIndex: 0 | 1) =>
    Array.from({ length: CARDS_PER_PAGE }, (_, j) => {
      const i = pageIndex * CARDS_PER_PAGE + j;
      const card = spread[i];
      if (!card) {
        return <div key={j} className="bestiary-card bestiary-card--back" />;
      }
      const { row, col } = gridPos(i);
      return (
        <BestiaryCard
          key={card.config.configId}
          card={card}
          navRow={row}
          navCol={col}
        />
      );
    });

  return (
    <div className="bestiary-overlay">
      <NineSliceFrame>
        <div className="bestiary-screen">
          <div className="bestiary-header">
            <h1 className="bestiary-title">Classeur</h1>
            <div className="bestiary-count">
              {cards.length} {cards.length > 1 ? 'cartes' : 'carte'}
            </div>
          </div>

          {cards.length === 0 ? (
            <div className="bestiary-empty">
              Aucun monstre vaincu pour l'instant. Plonge dans un donjon !
            </div>
          ) : (
            <div className="bestiary-binder" ref={containerRef}>
              <div className="bestiary-page">{renderPage(0)}</div>
              <div className="bestiary-spine" />
              <div className="bestiary-page">{renderPage(1)}</div>
            </div>
          )}

          <div className="bestiary-footer">
            {cards.length > 0 && (
              <div className="bestiary-hint">
                <KeyDisplay context="interface" action="CONFIRM" size="sm" />{' '}
                agrandir
              </div>
            )}
            <div className="bestiary-hint">
              <KeyDisplay context="interface" action="CANCEL" size="sm" />{' '}
              retour
            </div>
          </div>
        </div>
      </NineSliceFrame>

      {lightbox && (
        <BestiaryCardLightbox
          card={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
};
